import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NewsWidget from '../components/news/NewsWidget';
import extensions from '../extensions';
import { useQueryBuilderOverlay } from '../context/QueryBuilderOverlayContext';
import { useImageToolsOverlay } from '../context/ImageToolsOverlayContext';
import { useConeSearchOverlay } from '../context/ConeSearchOverlayContext';
import { useQueryHistoryOverlay } from '../context/QueryHistoryOverlayContext';
import { getImageCollections } from '../api/images';

const FeatureCard = ({ icon, title, description }) => {
  return (
    <Link
      className="relative flex flex-col items-center p-6 rounded-xl border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card shadow-md hover:shadow-lg transition-all group overflow-hidden"
    >
      {/* Simple bottom fade */}
      <div className="absolute bottom-0 left-0 w-full h-1/5 pointer-events-none bg-gradient-to-t from-ui-light-detail/40 to-transparent dark:from-ui-dark-detail/40" />
      <div className="absolute bottom-0 left-0 w-full h-[2px] pointer-events-none bg-ui-light-detail dark:bg-ui-dark-detail" />

      <div
        className="
          p-4 mb-4 rounded-full 
          bg-ui-light-detail/40 dark:bg-ui-dark-detail/40 
          text-ui-light-text-primary dark:text-ui-dark-text-primary
          group-hover:bg-primary-500 group-hover:text-white
          transition-colors
        "
      >
        {icon}
      </div>

      <h3 className="mb-2 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
        {title}
      </h3>

      <p className="text-center text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
        {description}
      </p>
    </Link>
  );
};

const Home = () => {
  const { isOpen: isQueryOpen, openOverlay: openQueryOverlay, closeOverlay: closeQueryOverlay } = useQueryBuilderOverlay();
  const { isOpen: isImageToolsOpen, openOverlay: openImageToolsOverlay, closeOverlay: closeImageToolsOverlay } = useImageToolsOverlay();
  const { isOpen: isConeSearchOpen, openOverlay: openConeSearchOverlay, closeOverlay: closeConeSearchOverlay } = useConeSearchOverlay();
  const { isOpen: isQueryHistoryOpen, openOverlay: openQueryHistoryOverlay, closeOverlay: closeQueryHistoryOverlay } = useQueryHistoryOverlay();
  const [firstCollectionId, setFirstCollectionId] = useState(null);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const collections = await getImageCollections();
        if (collections && collections.length > 0) {
          setFirstCollectionId(collections[0].id);
        }
      } catch (error) {
        console.error('Error fetching collections:', error);
      }
    };
    fetchCollections();
  }, []);

  const handleToggleToolsOverlay = () => {
    const anyOpen = isQueryOpen || isImageToolsOpen || isConeSearchOpen || isQueryHistoryOpen;
    if (anyOpen) {
      closeQueryOverlay();
      closeImageToolsOverlay();
      closeConeSearchOverlay();
      closeQueryHistoryOverlay();
    } else {
      openQueryOverlay({ minimized: true });
      openConeSearchOverlay({ minimized: true });
      openQueryHistoryOverlay({ minimized: true });
      if (firstCollectionId) {
        openImageToolsOverlay({ collectionId: firstCollectionId, minimized: true });
      }
    }
  };

  return (
    <div className="min-h-screen bg-ui-light-background dark:bg-ui-dark-background pb-12">
      {/* Hero Section with Grsottas background */}
      <div className="relative overflow-hidden rounded-lg">
        <div className="absolute inset-0">
          {/* <img
            src="https://splus.cloud/files/site_images/showcase/grsotnas_HYDRA-0051-153.68%20-%2028.91-1000_lupton.png"
            alt="S-PLUS astronomical survey"
            className="w-full h-full object-cover opacity-20"
          /> */}
        </div>
        <div className="relative px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center">
            <img
              src="https://escola.cbpf.br/logos/cbpf-logo.png"
              alt="CBPF Logo"
              className="h-14 mx-auto mb-6 invert dark:invert-0"
            />
            <h1 className="text-4xl font-bold tracking-tight text-ui-light-text-primary dark:text-ui-dark-text-primary sm:text-5xl">
              <span className="block">AI-Scope Portal</span>
              <span className="block text-primary-400 dark:text-primary-300 mt-2">Smart Cosmic Exploration with AI</span>
            </h1>
            <p className="max-w-2xl mx-auto mt-6 text-lg text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              Query astronomical databases, explore surveys, and build AI workflows for cosmic data analysis.
            </p>
            <div className="flex justify-center mx-auto mt-8 flex-wrap gap-4">
              <Link
                to="/query"
                className="px-6 py-3 text-base font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 transition-colors shadow-lg"
              >
                Start Querying
              </Link>
              <button
                onClick={handleToggleToolsOverlay}
                className="px-6 py-3 text-base font-medium text-white bg-primary-700/70 backdrop-blur-sm border border-primary-200/20 rounded-md hover:bg-primary-600 transition-colors shadow-lg flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Tools Overlay
              </button>
              <Link
                to="/documentation/python"
                className="px-6 py-3 text-base font-medium text-white bg-primary-800/50 backdrop-blur-sm border border-primary-200/20 rounded-md hover:bg-primary-700/50 transition-colors shadow-lg"
              >
                Python Workflows
              </Link>
            </div>
          </div>
        </div>
      </div>


{/* Extension widgets */}
      {extensions.map((ExtWidget, i) => (
        <ExtWidget key={i} />
      ))}


{/* Arco divider using mask-image */}
<div className="relative flex justify-center">
  <div
    className="relative w-full max-w-[1200px] h-72"
    style={{
      WebkitMaskImage: "url('/files/arcobl.png')",
      maskImage: "url('/files/arcobl.png')",
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskSize: "cover",
      maskSize: "cover",
      WebkitMaskPosition: "center bottom",
      maskPosition: "center bottom",
    }}
  >
    {/* Ultra-strong glow */}
    <div
      className="
        absolute inset-0
        bg-gradient-to-t
        from-ui-light-detail/95 via-ui-light-detail/60 via-ui-light-detail/20 to-transparent
        dark:from-ui-dark-detail/95 dark:via-ui-dark-detail/100 dark:via-ui-dark-detail/100 dark:to-transparent
        blur-[40px] brightness-[1.35]
      "
    />

    {/* Wider side fades */}
    <div
      className="
        pointer-events-none
        absolute inset-y-0 left-0 w-[45%]
        bg-gradient-to-r
        from-ui-light-background via-ui-light-background/80 to-transparent
        dark:from-ui-dark-background dark:via-ui-dark-background/80 dark:to-transparent
      "
    />
    <div
      className="
        pointer-events-none
        absolute inset-y-0 right-0 w-[45%]
        bg-gradient-to-l
        from-ui-light-background via-ui-light-background/80 to-transparent
        dark:from-ui-dark-background dark:via-ui-dark-background/80 dark:to-transparent
      "
    />
  </div>
</div>

      {/* Core Features */}
      <div className="py-12 bg-ui-light-background dark:bg-ui-dark-background">
        <div className="px-4 mx-auto max-w-6xl sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              Research Tools
            </h2>
            <p className="mt-3 text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
              Access survey data with SQL/ADQL queries, cross-matching, and visualization capabilities.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <FeatureCard
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
              title="SQL & ADQL Queries"
              description="Run complex queries on astronomical catalogs with standard database languages and astronomical extensions."
            />
            <FeatureCard
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              }
              title="Multi-Survey Access"
              description="Browse data from S-PLUS, SDSS, and other major astronomical surveys through unified interfaces."
            />
            <FeatureCard
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              }
              title="AI Workflows"
              description="Build intelligent analysis pipelines with machine learning tools for astronomical data processing."
            />
          </div>
        </div>
      </div>

      

      {/* News Section */}
      <div className="py-12 bg-ui-light-card dark:bg-ui-dark-card">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              Latest Updates
            </h2>
          </div>
          <NewsWidget />
        </div>
      </div>

      {/* About Section */}
      <div className="py-12 bg-ui-light-background dark:bg-ui-dark-background">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:justify-between lg:gap-12">
            <div className="lg:w-2/3">
              <h2 className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
                Built for Astronomical Research
              </h2>
              <p className="mt-4 text-lg text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                AI-Scope (SCoPE-AI) provides researchers with direct access to astronomical databases and 
                machine learning tools through both web interfaces and Python libraries.
              </p>
              
              <div className="mt-6">
                <Link
                  to="/documentation/python"
                  className="inline-flex items-center px-4 py-2 text-base font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded-md hover:bg-primary-100 dark:hover:bg-primary-800/50 transition-colors mr-4"
                >
                  Python Package Documentation
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </Link>
                <Link
                  to="/database"
                  className="inline-flex items-center px-4 py-2 text-base font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                >
                  Browse Database
                </Link>
              </div>
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-6 h-6 bg-primary-600 rounded-full">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Python workflows & AI tools</h3>
                    <p className="mt-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">Professional ML pipelines for object classification and discovery</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-6 h-6 bg-primary-600 rounded-full">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Cone searches and region queries</h3>
                    <p className="mt-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">Search by coordinates, object names, or custom regions</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-6 h-6 bg-primary-600 rounded-full">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Export and download</h3>
                    <p className="mt-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">Get results in CSV, FITS, JSON, or other standard formats</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-10 lg:mt-0 lg:w-1/3">
              <div className="bg-ui-light-card dark:bg-ui-dark-card p-6 rounded-lg shadow-lg border border-ui-light-border dark:border-ui-dark-border">
                <div className="flex items-center justify-center mb-4">
                  <img 
                    src="https://escola.cbpf.br/logos/cbpf-logo.png"
                    alt="CBPF Logo" 
                    className="h-12 invert dark:invert-0"
                  />
                </div>
                <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary text-center mb-4">
                  Developed in partnership with the Brazilian Center for Research in Physics (CBPF)
                </p>
                <div className="text-center">
                  <a 
                    href="https://portal.cbpf.br/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 rounded-md hover:bg-primary-100 dark:hover:bg-primary-800/50 transition-colors"
                  >
                    Learn More
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-primary-700 dark:bg-primary-800">
        <div className="px-4 py-10 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-bold text-white">
            <span className="block">Ready to start?</span>
            <span className="block text-xl font-normal text-primary-200 mt-1">Access astronomical data now</span>
          </h2>
          <div className="flex mt-6 lg:mt-0 gap-3">
            <Link
              to="/register"
              className="px-6 py-3 text-base font-medium text-primary-700 bg-white rounded-md hover:bg-primary-50 transition-colors"
            >
              Create Account
            </Link>
            <Link
              to="/query"
              className="px-6 py-3 text-base font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-500 transition-colors"
            >
              Start Querying
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;